import React from 'react';
import {
    Typography,
    Box,
    List,
    ListItem,
    ListItemButton,
    Chip,
    Button,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { getPriorityColor, getPriorityLabel } from '../../models/TaskDTO';
import WidgetCard from './WidgetCard';
import InsightsIcon from '@mui/icons-material/Insights';
import { useTasksServiceGetApiTasksPaginated } from '../../api/generated/queries/queries';

const RecentTasksWidget: React.FC = () => {
    // Fetch recent tasks (first 5)
    const { data: taskPagedResponse, isLoading } = useTasksServiceGetApiTasksPaginated({
        page: 0,
        size: 5,
        view: 'ALL',
        sortBy: 'CREATED_AT',
        sortDirection: 'DESC'
    });

    // Extract data from paginated response
    const tasks = taskPagedResponse?.content || [];

    return (
        <WidgetCard>
            <Box
                mb={1.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                pb={1}
                borderBottom="1px solid"
                borderColor="divider"
            >
                <Box display="flex" alignItems="center">
                    <Box
                        sx={{
                            backgroundColor: '#e3f2fd',
                            borderRadius: '50%',
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1,
                        }}
                    >
                        <InsightsIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600} fontSize="0.95rem">
                        Ostatnie zadania
                    </Typography>
                </Box>

                <Button
                    component={Link}
                    to="/tasks"
                    size="small"
                    variant="outlined"
                    sx={{
                        borderRadius: '999px',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        px: 1.5,
                        py: 0.25,
                        height: '24px',
                        minWidth: 'auto',
                        lineHeight: 1,
                    }}
                >
                    Zobacz wszystkie
                </Button>
            </Box>

            <List disablePadding>
                {isLoading ? (
                    <ListItem disableGutters sx={{ px: 0, py: 1 }}>
                        <Typography fontSize="0.875rem">Ładowanie...</Typography>
                    </ListItem>
                ) : tasks.length > 0 ? tasks.map((task, index) => (
                    <ListItem key={task.id}
                              disablePadding
                              sx={{
                                  borderBottom: index !== tasks.length - 1 ? '1px solid' : 'none',
                                  borderColor: 'divider',
                              }}
                    >
                        <ListItemButton
                            component={Link}
                            to={`/tasks?taskId=${task.id}`}
                            sx={{
                                px: 0,
                                py: 0.5,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                    borderRadius: 1,
                                },
                                transition: 'background-color 0.2s ease',
                                textDecoration: 'none',
                            }}
                        >
                            <Typography
                                fontSize="0.85rem"
                                fontWeight={500}
                                sx={{
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {task.title}
                            </Typography>
                            <Chip
                                size="small"
                                label={getPriorityLabel(task.priority)}
                                sx={{
                                    bgcolor: getPriorityColor(task.priority),
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    height: 22,
                                    ml: 1,
                                    pointerEvents: 'none', // Zapobiega kliknięciom na chip
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                )) : (
                    <ListItem disableGutters sx={{ px: 0, py: 1 }}>
                        <Typography fontSize="0.875rem">
                            Brak ostatnich zadań
                        </Typography>
                    </ListItem>
                )}
            </List>
        </WidgetCard>
    );
};

export default RecentTasksWidget;